import type { BadgeTone } from '@wundo/ui';
import type { SendableNotificationChannel } from '@wundo/shared/notificationChannel.ts';
import type {
  NotificationDeliveryItem,
  NotificationFailureReason,
  NotificationSendCounts,
} from '@wundo/shared/notificationHistory.ts';

export const HISTORY_CHANNEL_LABELS: Record<SendableNotificationChannel, string> = {
  msteams: 'Teams',
  slack: 'Slack',
  email: 'Email',
};

/*
 * Why somebody did not get it, in the words of the situation. A record, so a
 * reason added in the backend cannot reach this page without a label.
 */
export const FAILURE_LABELS: Record<NotificationFailureReason, string> = {
  msteams: 'Sent in Teams',
  slack: 'Sent in Slack',
  email: 'Sent by email',
  no_teams_chat: 'Not reachable: no Teams chat with Wundo',
  no_slack_chat: 'Not reachable: no Slack DM with Wundo',
  no_webapp: 'Their organisation does not use the web app',
  teams_only: 'Only sent in Teams, and they are on another channel',
  teams_or_email_only: 'Only sent in Teams or by email, and they are on Slack',
  no_email_address: 'No email address',
  opted_out: 'Unsubscribed from Wundo emails',
  channel_none: 'Notification channel set to none',
  settled: 'Had already received it',
  cannot_see_roleplay: 'Does not have this roleplay on their practice page',
  already_played: 'Already did this roleplay',
  roleplay_unavailable: 'The roleplay was no longer available',
  nothing_to_say: 'Nothing to tell them yet',
  magic_link_disabled: 'Organisation signs in with SSO',
  expired: 'Pilot ended, account closed',
  send_failed: 'Sending failed',
};

export interface DeliveryStatus {
  label: string;
  tone: BadgeTone;
}

/* The worst news first: a failure, then what the mail provider reported. */
export function deliveryStatus(delivery: NotificationDeliveryItem): DeliveryStatus {
  if (delivery.status === 'failed') return { label: 'Failed', tone: 'danger' };
  if (delivery.outcome === 'complained') {
    return { label: 'Marked as spam', tone: 'danger' };
  }
  if (delivery.outcome === 'bounced') return { label: 'Bounced', tone: 'danger' };
  if (delivery.outcome === 'delivered') return { label: 'Delivered', tone: 'success' };
  return { label: 'Sent', tone: 'neutral' };
}

export interface CountPart {
  key: keyof NotificationSendCounts;
  text: string;
  problem: boolean;
}

const COUNT_ORDER: {
  key: keyof NotificationSendCounts;
  word: string;
  problem: boolean;
}[] = [
  { key: 'delivered', word: 'delivered', problem: false },
  { key: 'sent', word: 'sent', problem: false },
  { key: 'bounced', word: 'bounced', problem: true },
  { key: 'complained', word: 'marked as spam', problem: true },
  { key: 'failed', word: 'failed', problem: true },
];

/* "28 delivered · 1 bounced · 1 failed", leaving out what did not happen. */
export function countParts(counts: NotificationSendCounts): CountPart[] {
  return COUNT_ORDER.filter(({ key }) => counts[key] > 0).map(
    ({ key, word, problem }) => ({ key, text: `${counts[key]} ${word}`, problem }),
  );
}

export function personLabel(delivery: Pick<NotificationDeliveryItem, 'name' | 'email'>) {
  return delivery.name || delivery.email || 'Unknown person';
}

const DAY_FORMAT = { weekday: 'short', day: 'numeric', month: 'short' } as const;

/*
 * A press in the console happened at a moment; an automatic send is a whole
 * day of them, so its time would be the first one's and mean nothing.
 */
export function sendDate(sentAt: string, withTime: boolean): string {
  const date = new Date(sentAt);
  const options: Intl.DateTimeFormatOptions = {
    ...DAY_FORMAT,
    ...(date.getFullYear() === new Date().getFullYear() ? {} : { year: 'numeric' }),
    ...(withTime ? { hour: '2-digit', minute: '2-digit' } : {}),
  };
  return date.toLocaleString(undefined, options);
}

export function fullDateTime(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** How long the content of what somebody received is kept before the nightly job wipes it. */
export const CONTENT_KEPT_DAYS = 180;

export function contentKeptUntil(sentAt: string): string {
  const until = new Date(sentAt);
  until.setDate(until.getDate() + CONTENT_KEPT_DAYS);
  return until.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}
